import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaCarAlt,
  FaRoute,
  FaCalendarAlt,
  FaUserCheck,
  FaMapMarkerAlt,
  FaUser,
  FaMoneyBillWave,
  FaChartLine,
  FaWallet,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import DriverLayout from "../../layouts/DriverLayout";
import { isObjectId } from "../../utils/objectIdUtils";
import {
  getDriverProfile,
  getBookingHistory,
  updateDriverLocation,
  reset,
} from "../../features/driver/driverSlice";
import {
  getDriverWallet,
  reset as resetWallet,
} from "../../features/wallet/walletSlice";
import socketService from "../../utils/socketService";
import WalletCard from "../../components/Driver/WalletCard";
import RideRequestModal from "../../components/Driver/RideRequestModal";
import { isDriverAuthenticated } from "../../utils/driverAuthUtils";
import { API_URL } from "../../config/apiConfig";

import { clearDriverAuth } from "../../features/driver/driverAuthSlice";



const DriverDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { driver: driverAuth } = useSelector((state) => state.driverAuth);
  const { driver, bookingHistory, loading, error, success, message } =
    useSelector((state) => state.driver);
  const lastToastRef = useRef({ msg: null, at: 0 });
  const { wallet, isError: walletError, message: walletMessage } = useSelector((state) => state.wallet);

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [historyRequested, setHistoryRequested] = useState(false);
  const [pendingRideRequest, setPendingRideRequest] = useState(null);
  const [showRideRequestModal, setShowRideRequestModal] = useState(false);
  // Full list of available ride requests shown as cards on the dashboard
  const [pendingRidesList, setPendingRidesList] = useState([]);

  // Notify DriverLayout sidebar of pending ride count via custom window event
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("pendingRidesUpdate", { detail: { count: pendingRidesList.length } })
    );
    // Cleanup: reset count to 0 on unmount
    return () => {
      window.dispatchEvent(new CustomEvent("pendingRidesUpdate", { detail: { count: 0 } }));
    };
  }, [pendingRidesList.length]);

  // Security check - verify authentication on dashboard load
  useEffect(() => {
    if (!isDriverAuthenticated({ driver: driverAuth })) {
      dispatch(clearDriverAuth());
      navigate('/driver/login', { replace: true });
      return;
    }
  }, [driverAuth, dispatch, navigate]);


  // Initialize socket connection for real-time updates - separated into connect and event listener phases
  // Socket initialization - only runs once when component mounts
  useEffect(() => {
    // Connect to socket server - this is a singleton now and will only connect once
    socketService.connect();

    // We'll handle the event listeners in a separate useEffect
    // No cleanup here - we want to keep the socket alive for the entire app session
  }, []); // Empty dependency array means this only runs once

  // State for current assignment moved to the top level of the component

  // Set up event listeners when driver auth changes
  useEffect(() => {
    let cleanDriverAssigned = () => {};
    let cleanNewRideRequest = () => {};
    let cleanRideTaken = () => {};
    let cleanAutoAssignExpired = () => {};

    if (driverAuth) {
      // Get the driver ID - now that we store driver data directly, check both patterns
      const driverId =
        driverAuth._id ||
        (driverAuth.driver && driverAuth.driver._id) ||
        driverAuth.id ||
        (driverAuth.driver && driverAuth.driver.id);

      if (driverId) {
        // Only join if we haven't already joined for this driver

        // Join driver room to receive updates
        socketService.joinDriverRoom(driverId);

        // Listen for driver assignments with a specific handler for this component
        const driverAssignmentHandler = (data) => {

          // Verify this assignment is for the current driver
          if (data.driver._id === driverId) {
            setCurrentAssignment(data);

            // Show notification
            toast.success("You have been assigned a new ride!", {
              onClick: () => {
                if (data && data.booking && typeof window !== 'undefined') {
                  sessionStorage.setItem("selectedBookingId", data.booking._id || data.booking.bookingId);
                }
                navigate("/driver/current-ride");
              },
            });
          }
        };

        // Listen for new ride requests from auto-assign
        // On dashboard we ONLY update the badge count — no popup, no toast
        // The /driver/new-ride-requests page handles the full UI
        const newRideRequestHandler = (data) => {
          console.log('[Dashboard] New ride request received (badge only):', data);
          // Add to list for sidebar badge — avoid duplicates
          setPendingRidesList(prev => {
            const exists = prev.some(r => r.bookingId === data.bookingId);
            return exists ? prev : [data, ...prev];
          });
        };

        // Listen for ride taken notifications — remove from badge list, no toast on dashboard
        const rideTakenHandler = (data) => {
          if (data?.bookingId) {
            setPendingRidesList(prev => prev.filter(r => r.bookingId !== data.bookingId));
          }
          console.log('[Dashboard] Ride taken by another driver:', data);
        };

        // Listen for auto-assign expiration — remove from badge list, no toast on dashboard
        const autoAssignExpiredHandler = (data) => {
          if (data?.bookingId) {
            setPendingRidesList(prev => prev.filter(r => r.bookingId !== data.bookingId));
          }
          console.log('[Dashboard] Auto-assign expired:', data);
        };

        // Register the event handlers and store their cleanup functions
        cleanDriverAssigned = socketService.onDriverAssigned(driverAssignmentHandler);
        cleanNewRideRequest = socketService.onNewRideRequest(newRideRequestHandler);
        cleanRideTaken = socketService.onRideTaken(rideTakenHandler);
        cleanAutoAssignExpired = socketService.onAutoAssignExpired(autoAssignExpiredHandler);
      }
    }

    // Clean up event listeners when component unmounts or auth changes
    return () => {
      cleanDriverAssigned();
      cleanNewRideRequest();
      cleanRideTaken();
      cleanAutoAssignExpired();
    };
  }, [driverAuth, navigate]);

  // 1. Initial Load of Driver Profile when driverAuth is available
  useEffect(() => {
    if (driverAuth) {
      const driverId =
        driverAuth._id ||
        (driverAuth.driver && driverAuth.driver._id) ||
        driverAuth.id ||
        (driverAuth.driver && driverAuth.driver.id);

      if (driverId) {
        if (!isObjectId(String(driverId))) {
          toast.error("Invalid driver session. Please log in again.");
          dispatch(clearDriverAuth());
          navigate('/driver/login', { replace: true });
          setLoadingProfile(false);
          return;
        }
        dispatch(getDriverProfile(driverId));
      } else {
        toast.error("Unable to load driver profile. Please try logging in again.");
        setLoadingProfile(false);
      }
    } else {
      setLoadingProfile(false);
    }

    return () => {
      dispatch(reset());
    };
  }, [dispatch, driverAuth, navigate]);

  // 2. Initial Load of Booking History & Wallet once Driver Profile ID is available
  useEffect(() => {
    const driverId = driver?._id;
    if (driverId && isObjectId(String(driverId))) {
      setLoadingProfile(false);
      if (!bookingHistory) {
        dispatch(getBookingHistory(driverId));
      }
      dispatch(getDriverWallet(driverId)).catch((error) => {
        console.error("Failed to load driver wallet on mount:", error);
      });
    }
  }, [dispatch, driver?._id, bookingHistory]);

  // 3. Periodic Driver Profile Refresh every 30 seconds
  useEffect(() => {
    const driverId = driver?._id;
    if (!driverId || !isObjectId(String(driverId))) return;

    const profileInterval = setInterval(() => {
      dispatch(getDriverProfile(driverId));
    }, 30000);

    return () => clearInterval(profileInterval);
  }, [dispatch, driver?._id]);

  // 4. Periodic Wallet Refresh every 30 seconds
  useEffect(() => {
    const driverId = driver?._id;
    if (!driverId || !isObjectId(String(driverId))) return;

    const walletInterval = setInterval(() => {
      dispatch(getDriverWallet(driverId)).catch((error) => {
        console.error("Failed to refresh driver wallet:", error);
      });
    }, 30000);

    return () => clearInterval(walletInterval);
  }, [dispatch, driver?._id]);

  useEffect(() => {
    if (error) {
      // Suppress repeated identical toasts
      const now = Date.now();
      const sameAsLast = lastToastRef.current.msg === error && (now - lastToastRef.current.at) < 4000;

      // If error is 'Driver not found', treat it as non-fatal: keep session alive and retry fetching profile
      const isDriverNotFound = typeof error === 'string' && /Driver not found/i.test(error);
      const isAuthError = typeof error === 'string' && /MISSING_AUTH_TOKEN|INVALID_TOKEN|TOKEN_EXPIRED|ACCOUNT_NOT_FOUND/i.test(error);

      if (isDriverNotFound) {
        // Log for debugging and avoid showing a frightening toast to the driver
        console.warn('Non-fatal: Driver not found while fetching profile. Will retry silently.');

        // Retry fetching driver profile once after a short delay (10s)
        setTimeout(() => {
          try {
            const driverId = driverAuth?._id || driverAuth?.driver?._id || driverAuth?.id || driverAuth?.driver?.id;
            if (driverId) dispatch(getDriverProfile(driverId));
          } catch (e) {
            console.warn('Retry for driver profile failed to start:', e.message || e);
          }
        }, 10000);

        // Do not clear auth or navigate away; simply reset state silently
        dispatch(reset());
        setLoadingProfile(false);
        return; // Early return — don't show toast for this specific case
      }

      if (!sameAsLast) {
        // Show error for other (likely important) errors
        toast.error(error);
        lastToastRef.current = { msg: error, at: now };
      }

      // If it's an authentication-related error, clear session and force re-login
      if (isAuthError) {
        dispatch(clearDriverAuth());
        navigate('/driver/login', { replace: true });
      }

      dispatch(reset());
      setLoadingProfile(false);
    }

    if (success) {
      const now = Date.now();
      const sameAsLast = lastToastRef.current.msg === message && (now - lastToastRef.current.at) < 4000;
      if (!sameAsLast) {
        toast.success(message);
        lastToastRef.current = { msg: message, at: now };
      }
      dispatch(reset());
    }

    // Set loading to false when we get the driver profile
    if (driver && driver._id) {
      setLoadingProfile(false);
    }
  }, [error, success, message, dispatch, driver, navigate]);

  // 5. Periodic Location Tracking (every 60 seconds) when driver is online (available)
  useEffect(() => {
    const driverId = driver?._id;
    const isAvailable = driver?.isAvailable;
    if (!driverId || !isAvailable) return;

    const updateLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            dispatch(
              updateDriverLocation({
                driverId,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
              }),
            );
          },
          // Silent error handling
          () => {},
          {
            enableHighAccuracy: false,
            maximumAge: 30000,
            timeout: 20000,
          },
        );
      }
    };

    // Update location immediately when starting tracking
    updateLocation();

    // Set up periodic updates
    const intervalId = setInterval(updateLocation, 60000);

    return () => {
      clearInterval(intervalId);
    };
  }, [dispatch, driver?._id, driver?.isAvailable]);

  // State for current assignment (populated by socket)
  const [currentAssignment, setCurrentAssignment] = useState(null);
  // We'll calculate revenue from actual booking data later

  // Handle ride request modal close
  const handleCloseRideRequestModal = () => {
    if (pendingRideRequest?.timeoutId) {
      clearTimeout(pendingRideRequest.timeoutId);
    }
    setShowRideRequestModal(false);
    setPendingRideRequest(null);
  };

  // Handle ride acceptance
  const handleAcceptRideRequest = (booking) => {
    // Remove the accepted ride from the card list
    if (pendingRideRequest?.bookingId) {
      setPendingRidesList(prev => prev.filter(r => r.bookingId !== pendingRideRequest.bookingId));
    }
    setPendingRidesList([]); // Clear all pending since driver is now assigned
    if (booking?._id && typeof window !== 'undefined') {
      sessionStorage.setItem("selectedBookingId", booking._id);
    }
    // Navigate to current ride page to show the booking details
    navigate("/driver/current-ride");
  };

  // Pending ride requests poll — updates badge count only (no modal on dashboard)
  useEffect(() => {
    if (!driver || !driver._id) return;

    const driverId = driver._id;
    const token = localStorage.getItem('driverToken');
    if (!token) return;

    const checkPendingRides = async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/driver/${driverId}/pending-ride-requests`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (data.success && data.data) {
          if (data.count > 0) {
            // Merge into list — only for sidebar badge, no modal
            setPendingRidesList(prev => {
              const existingIds = new Set(prev.map(r => r.bookingId));
              const newRides = data.data.filter(r => !existingIds.has(r.bookingId));
              return newRides.length > 0 ? [...newRides, ...prev] : prev;
            });
          } else {
            setPendingRidesList([]);
          }
        }
      } catch (pollErr) {
        // Non-critical
        console.warn('[Dashboard] Pending ride poll failed:', pollErr?.message || pollErr);
      }
    };

    const pollTimer = setTimeout(checkPendingRides, 2000);
    const pollInterval = setInterval(checkPendingRides, 30000);
    return () => {
      clearTimeout(pollTimer);
      clearInterval(pollInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver?._id]);

  // Open modal for a specific ride from the card list
  const handleOpenRideFromList = (rideData) => {
    setPendingRideRequest(rideData);
    setShowRideRequestModal(true);
  };



  // Make sure booking history is fetched if not already loaded and refresh driver profile
  useEffect(() => {
    if (driver && driver._id && !historyRequested) {
      dispatch(getBookingHistory(driver._id));
      setHistoryRequested(true);

      // Also refresh driver profile to ensure current status is accurate
      dispatch(getDriverProfile(driver._id));
    }
  }, [dispatch, driver, historyRequested]);

  // Helper functions for date calculations
  const getStartOfWeek = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay()); // Start of current week (Sunday)
    startOfWeek.setHours(0, 0, 0, 0); // Set to midnight
    return startOfWeek;
  };

  const getStartOfMonth = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1); // First day of current month
    startOfMonth.setHours(0, 0, 0, 0); // Set to midnight
    return startOfMonth;
  };

  const getBookingsThisWeek = () => {
    if (!bookingHistory || bookingHistory.length === 0) return 0;
    const startOfWeek = getStartOfWeek();

    const allThisWeek = bookingHistory.filter(
      (booking) => new Date(booking.createdAt) >= startOfWeek,
    );
    return allThisWeek.length;
  };

  const getBookingsThisMonth = () => {
    if (!bookingHistory || bookingHistory.length === 0) return 0;
    const startOfMonth = getStartOfMonth();

    const allThisMonth = bookingHistory.filter(
      (booking) => new Date(booking.createdAt) >= startOfMonth,
    );
    return allThisMonth.length;
  };

  // Helper function to get consistent total ride count
  const getConsistentTotalRides = () => {
    // Use driver.totalRides from database to match admin panel
    const databaseTotalRides = driver?.totalRides || 0;

    if (bookingHistory && Array.isArray(bookingHistory)) {
      // If there's a significant discrepancy, show both values
      if (Math.abs(databaseTotalRides - bookingHistory.length) > 5) {
        // Optional: handle discrepancy
      }
    }

    // Return database value to match admin panel
    return databaseTotalRides;
  };



  return (
    <DriverLayout>
      {loading || loadingProfile ? (
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary"></div>
          <div className="ml-4 text-gray-700 font-medium">
            Loading driver dashboard...
          </div>
        </div>
      ) : !driver || !driver._id ? (
        <div className="flex flex-col justify-center items-center h-96">
          <div className="text-red-500 text-xl mb-4">
            Unable to load driver profile
          </div>
          <button
            onClick={() => (window.location.href = "/driver/login")}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
          >
            Return to Login
          </button>
        </div>
      ) : (!(driver.documentsVerified === true || driver.documentsVerified === "true") || !(driver.isApproved === true || driver.isApproved === "true")) ? (
        <div className="max-w-xl mx-auto py-12">
          <div className="bg-white border border-rose-100 rounded-3xl p-8 shadow-xl text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-rose-400 to-orange-450" />
            <div className="w-20 h-20 mx-auto rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center text-4xl mb-6">
              ⚠️
            </div>
            <h2 className="text-2xl font-black text-slate-800 mb-3">
              {!(driver.isApproved === true || driver.isApproved === "true")
                ? "Account Pending Approval"
                : "Documents Under Verification"}
            </h2>
            <p className="text-slate-500 mb-8 text-sm leading-relaxed max-w-md mx-auto">
              {!(driver.isApproved === true || driver.isApproved === "true")
                ? "Your registration has been received successfully. Our administrators are currently reviewing your profile. We'll notify you as soon as your account is approved."
                : "Your uploaded documents are currently being checked by our verification team. This usually takes up to 24-48 hours. Please check back later."}
            </p>
            
            <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 text-left mb-8">
              <h3 className="font-extrabold text-xs text-slate-450 uppercase tracking-widest mb-3">Required Documents:</h3>
              <ul className="grid grid-cols-2 gap-3 text-xs font-semibold text-slate-650">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Driver's License
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Vehicle Registration
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Insurance Certificate
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Identity Proof
                </li>
              </ul>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <button
                onClick={() => navigate("/driver/profile")}
                className="px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-850 font-semibold text-sm transition-all duration-300"
              >
                Update Profile Documents
              </button>
              <button
                onClick={() => {
                  dispatch(clearDriverAuth());
                  navigate('/driver/login');
                }}
                className="px-6 py-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 font-semibold text-sm transition-all duration-300"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Welcome Banner */}
          <div className="relative overflow-hidden bg-slate-900 text-white rounded-3xl p-6 sm:p-8 shadow-xl border border-slate-800">
            <div className="absolute right-0 top-0 translate-x-12 -translate-y-12 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl" />
            <div className="absolute left-1/3 bottom-0 translate-y-12 w-48 h-48 bg-indigo-500/10 rounded-full blur-2xl" />
            
            <div className="relative flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-tr from-amber-500 to-amber-600 text-slate-950 flex items-center justify-center text-2xl font-black shadow-lg shadow-amber-500/20">
                    {(driverAuth?.name || driverAuth?.driver?.name)?.charAt(0).toUpperCase()}
                  </div>
                  <span className={`absolute bottom-0 right-0 block h-4 w-4 rounded-full border-2 border-slate-900 ${
                    driver?.isAvailable ? "bg-emerald-500" : "bg-rose-500"
                  }`} />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-black tracking-tight">
                    Welcome back, {driverAuth?.name || driverAuth?.driver?.name}!
                  </h2>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 items-center mt-1.5 text-slate-400 text-xs sm:text-sm">
                    <span>{driverAuth?.phone || driverAuth?.driver?.phone}</span>
                    <span className="hidden sm:inline text-slate-650">•</span>
                    <span>{driverAuth?.email || driverAuth?.driver?.email}</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <span className={`px-4 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                  driver?.isAvailable 
                    ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-lg shadow-emerald-500/5" 
                    : "bg-rose-500/10 text-rose-400 border border-rose-500/20"
                }`}>
                  {driver?.isAvailable ? "Online" : "Offline"}
                </span>
                {driver?.currentBooking && (
                  <span className="px-4 py-1.5 rounded-full bg-amber-500/20 text-amber-450 border border-amber-500/30 text-[10px] font-extrabold uppercase tracking-wider animate-pulse">
                    On Trip
                  </span>
                )}
              </div>
            </div>
          </div>


          {/* Details Panels */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Vendor Management Card */}
            {driver.isVendorDriver && (
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all duration-300">
                <h3 className="font-extrabold text-slate-800 text-sm mb-4 flex items-center gap-2">
                  <span className="text-indigo-500">🏢</span> Vendor Management
                </h3>
                <div className="p-4 rounded-xl bg-slate-50 border border-slate-150/50 flex flex-col gap-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Managed By</span>
                  <span className="font-extrabold text-slate-800 text-sm">
                    {driver.vendorId?.fullName || driver.vendorId?.businessName || 'Vendor Name Not Available'}
                  </span>
                  <p className="text-xs text-indigo-600 mt-1 font-medium leading-relaxed">
                    Your rides and payouts are assigned and coordinated through your vendor partner dashboard.
                  </p>
                </div>
              </div>
            )}

            {/* Vehicle Details Card */}
            <div className={`bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all duration-300 ${driver.isVendorDriver ? '' : 'lg:col-span-2'}`}>
              <h3 className="font-extrabold text-slate-800 text-sm mb-4 flex items-center gap-2">
                <span className="text-amber-500">🚗</span> Vehicle Details
              </h3>
              <div className="grid grid-cols-2 gap-y-4 gap-x-2">
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Type</span>
                  <span className="text-sm font-semibold text-slate-700 mt-0.5">
                    {driver.vehicleType
                      ? typeof driver.vehicleType === "object"
                        ? driver.vehicleType.name
                        : driver.vehicleType
                      : "Not specified"}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Model</span>
                  <span className="text-sm font-semibold text-slate-700 mt-0.5">{driver.vehicleModel}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Plate Number</span>
                  <span className="text-sm font-semibold text-slate-700 mt-0.5">{driver.vehicleNumber}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">License Expiry</span>
                  <span className="text-sm font-semibold text-slate-700 mt-0.5">
                    {new Date(driver.licenseExpiry).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    }).replace(/\//g, ' ')}
                  </span>
                </div>
              </div>
            </div>

            {/* Verification / Ratings Card */}
            <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all duration-300">
              <h3 className="font-extrabold text-slate-800 text-sm mb-4 flex items-center gap-2">
                <span className="text-emerald-500">🛡️</span> Verification & Profile
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                  <span className="text-xs text-slate-500">License Number</span>
                  <span className="text-xs font-bold text-slate-800">{driver.licenseNumber}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-slate-50">
                  <span className="text-xs text-slate-500">Documents Verified</span>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${
                    driver.documentsVerified === true || driver.documentsVerified === "true"
                      ? "text-emerald-600"
                      : "text-rose-600"
                  }`}>
                    {driver.documentsVerified === true || driver.documentsVerified === "true" ? (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Yes
                      </>
                    ) : (
                      <>
                        <span className="h-1.5 w-1.5 rounded-full bg-rose-500" /> Pending
                      </>
                    )}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1.5">
                  <span className="text-xs text-slate-500">Average Rating</span>
                  <span className="text-xs font-extrabold text-slate-800 flex items-center gap-1">
                    ⭐ {driver.ratings ? driver.ratings.toFixed(1) : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Sync Warning Notice */}
          {bookingHistory && bookingHistory.length > 0 && Math.abs((driver?.totalRides || 0) - bookingHistory.length) > 5 && (
            <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 flex gap-3 items-start">
              <span className="text-lg">⚠️</span>
              <div>
                <h4 className="text-xs font-extrabold text-orange-800 uppercase tracking-wider">Data Sync Notice</h4>
                <p className="text-xs text-orange-700 mt-1 leading-relaxed">
                  Profile summary shows {driver?.totalRides || 0} total rides, but booking history has {bookingHistory.length} records.
                  This won't affect active assignments.
                </p>
              </div>
            </div>
          )}

          {/* Performance Metrics */}
          <div>
            <h2 className="text-base font-extrabold text-slate-800 mb-4 tracking-tight">Performance Metrics</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Rides</span>
                    <span className="text-2xl font-black text-slate-855 mt-1">
                      {getConsistentTotalRides()}
                    </span>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-blue-50/50 flex items-center justify-center">
                    <FaCarAlt className="text-blue-500 text-lg" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">This Month</span>
                    <span className="text-2xl font-black text-slate-855 mt-1">
                      {getBookingsThisMonth()}
                    </span>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-emerald-50/50 flex items-center justify-center">
                    <FaRoute className="text-emerald-500 text-lg" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">This Week</span>
                    <span className="text-2xl font-black text-slate-855 mt-1">
                      {getBookingsThisWeek()}
                    </span>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-amber-50/50 flex items-center justify-center">
                    <FaCalendarAlt className="text-amber-500 text-lg" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Avg Rating</span>
                    <span className="text-2xl font-black text-slate-855 mt-1">
                      {driver?.ratings && driver.ratings > 0 ? driver.ratings.toFixed(1) : "N/A"}
                    </span>
                  </div>
                  <div className="w-12 h-12 rounded-xl bg-violet-50/50 flex items-center justify-center">
                    <FaUserCheck className="text-violet-500 text-lg" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Wallet Section */}
          <div className="space-y-4">
            {walletError && walletMessage && (
              <div className="bg-rose-50 border border-rose-100 rounded-2xl p-4 flex justify-between items-center">
                <div className="flex gap-3 items-center">
                  <span className="text-lg">⚠️</span>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-rose-800 uppercase tracking-wider">Wallet Service Issue</span>
                    <p className="text-xs text-rose-750 mt-0.5">{walletMessage}</p>
                  </div>
                </div>
                <button
                  onClick={() => driver?._id && dispatch(getDriverWallet(driver._id))}
                  className="bg-white border border-rose-200 hover:bg-rose-50 text-rose-800 text-xs font-bold px-3.5 py-1.5 rounded-xl transition"
                >
                  Retry Load
                </button>
              </div>
            )}
            <WalletCard
              wallet={wallet}
              driverId={driver?._id}
              onRefresh={() =>
                driver?._id && dispatch(getDriverWallet(driver._id))
              }
            />
          </div>

          {/* Show current assignment if received via socket */}
          {currentAssignment && (
            <div className="bg-white rounded-3xl border border-emerald-100 p-6 shadow-xl border-l-8 border-l-emerald-500">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-md">Live Assign</span>
                  <h2 className="text-lg font-black text-slate-855 mt-2">New Ride Assignment!</h2>
                </div>
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-500 animate-pulse">
                  🚗
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5 rounded-2xl bg-slate-50 border border-slate-100 mb-6">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Pickup Location</span>
                  <div className="flex items-start gap-2 mt-1.5">
                    <FaMapMarkerAlt className="text-rose-500 mt-1" />
                    <p className="text-sm font-semibold text-slate-700">
                      {currentAssignment.booking.pickupLocation?.name || "Location details not available"}
                    </p>
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Drop Location</span>
                  <div className="flex items-start gap-2 mt-1.5">
                    <FaMapMarkerAlt className="text-blue-500 mt-1" />
                    <p className="text-sm font-semibold text-slate-700">
                      {currentAssignment.booking.dropLocation?.name || "Location details not available"}
                    </p>
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Passenger</span>
                  <div className="flex items-center gap-2 mt-1.5">
                    <FaUser className="text-slate-400" />
                    <p className="text-sm font-semibold text-slate-700">
                      {currentAssignment.booking.user?.name || "Customer"} 
                      {currentAssignment.booking.user?.phone && ` (${currentAssignment.booking.user.phone})`}
                    </p>
                  </div>
                </div>

                <div>
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Service Car</span>
                  <div className="flex items-center gap-2 mt-1.5">
                    <FaCarAlt className="text-slate-400" />
                    <p className="text-sm font-semibold text-slate-700">
                      {currentAssignment.booking.cabType?.name || "Standard Class"}
                    </p>
                  </div>
                </div>

                {currentAssignment.booking.selectedAddOns && currentAssignment.booking.selectedAddOns.length > 0 && (
                  <div className="col-span-1 md:col-span-2 border-t border-slate-200/60 pt-4 mt-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block mb-2">📦 Selected Add-on Services (Optional)</span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {currentAssignment.booking.selectedAddOns.map((addon, index) => (
                        <div key={index} className="flex justify-between items-center text-xs font-semibold text-slate-700 bg-white/50 px-3 py-1.5 rounded-lg border border-slate-100">
                          <span className="truncate pr-2">{addon.name}</span>
                          <span className="text-slate-900 font-extrabold flex-shrink-0">₹{addon.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    if (currentAssignment && currentAssignment.booking && typeof window !== 'undefined') {
                      sessionStorage.setItem("selectedBookingId", currentAssignment.booking._id || currentAssignment.booking.bookingId);
                    }
                    navigate("/driver/current-ride");
                  }}
                  className="bg-slate-900 text-white px-6 py-3 rounded-xl hover:bg-slate-850 font-semibold text-sm transition-all duration-300 shadow-md"
                >
                  Accept & Open Navigation
                </button>
              </div>
            </div>
          )}

          {/* Recent Activity Card */}
          <div className="bg-white rounded-3xl border border-slate-100 p-6 shadow-sm">
            <h2 className="text-base font-extrabold text-slate-800 mb-6 tracking-tight">Recent Activity</h2>
            {bookingHistory && bookingHistory.length > 0 ? (
              <div className="space-y-4">
                {bookingHistory.slice(0, 3).map((booking, index) => {
                  let icon, iconClass, borderClass, title, subtitle;
                  if (booking.status === "completed") {
                    icon = <FaRoute className="text-blue-600 text-sm" />;
                    iconClass = "bg-blue-50";
                    borderClass = "border-l-blue-500";
                    title = "Trip Completed";
                    subtitle = `${booking.pickupLocation?.name || "Pickup"} to ${booking.dropLocation?.name || "Destination"}`;
                  } else if (booking.status === "cancelled") {
                    icon = <FaUser className="text-rose-600 text-sm" />;
                    iconClass = "bg-rose-50";
                    borderClass = "border-l-rose-500";
                    title = "Booking Cancelled";
                    subtitle = `${booking.pickupLocation?.name || "Pickup"} to ${booking.dropLocation?.name || "Destination"}`;
                  } else if (booking.status === "confirmed" || booking.status === "assigned") {
                    icon = <FaUserCheck className="text-emerald-600 text-sm" />;
                    iconClass = "bg-emerald-50";
                    borderClass = "border-l-emerald-500";
                    title = "New Booking Assigned";
                    subtitle = `${booking.pickupLocation?.name || "Pickup"} to ${booking.dropLocation?.name || "Destination"}`;
                  } else {
                    icon = <FaMoneyBillWave className="text-violet-600 text-sm" />;
                    iconClass = "bg-violet-50";
                    borderClass = "border-l-violet-500";
                    title = booking.status.charAt(0).toUpperCase() + booking.status.slice(1);
                    subtitle = `${booking.pickupLocation?.name || "Pickup"} to ${booking.dropLocation?.name || "Destination"}`;
                  }

                  const date = new Date(booking.createdAt);
                  const formattedDate = new Intl.DateTimeFormat("en-IN", {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  }).format(date);

                  return (
                    <div
                      key={index}
                      className={`flex items-start p-4 border-l-4 ${borderClass} bg-slate-50/50 hover:bg-slate-50 rounded-2xl transition duration-200`}
                    >
                      <div className={`${iconClass} w-9 h-9 rounded-xl flex items-center justify-center mr-4 flex-shrink-0`}>
                        {icon}
                      </div>
                      <div className="flex-grow">
                        <div className="flex justify-between items-start gap-4">
                          <p className="text-sm font-extrabold text-slate-800">{title}</p>
                          {booking.fare && (
                            <p className="text-sm font-black text-emerald-600">
                              ₹{booking.fare}
                            </p>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 mt-1 line-clamp-1">{subtitle}</p>
                        <p className="text-[10px] text-slate-400 mt-1 font-semibold">{formattedDate}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-slate-400 text-sm">No recent activities found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </DriverLayout>
  );
};

export default DriverDashboard;
